import RiskBadge from './ui/RiskBadge';
import {
  FEED_CARD_SHELL,
  FEED_INSPECT_BTN,
  FEED_LINK,
  FEED_TITLE,
} from './investigation/feedCardStyles';
import MatchBadge from './investigation/MatchBadge';
import {
  resolveHibpBreachCount,
  hibpRiskBadgeLabel,
} from '../utils/hibpBreachCount';

function hibpAccountUrl(res, fallbackEmail) {
  const inspect = String(res.inspect_url || res.url || '').trim();
  if (inspect && /haveibeenpwned\.com\/account\//i.test(inspect)) return inspect;
  const email =
    fallbackEmail ||
    res.target ||
    (typeof res.details?.misc === 'string' ? res.details.misc.split('|')[0]?.trim() : '') ||
    '';
  if (email && email.includes('@')) {
    return `https://haveibeenpwned.com/account/${encodeURIComponent(email)}`;
  }
  return 'https://haveibeenpwned.com/';
}

/**
 * Persistent HIBP status card — summary only; breach details live in Inspect modal.
 *
 * @param {{
 *   email?: string,
 *   status?: object,
 *   breaches?: object[],
 *   loading?: boolean,
 *   onInspect?: Function,
 * }} props
 */
export default function HibpCard({
  email,
  status,
  breaches = [],
  loading = false,
  onInspect,
}) {
  const unavailable = status?.status === 'unavailable' || Boolean(status?.api_error);
  const count = resolveHibpBreachCount([], { status, breaches });
  const clean = !unavailable && count === 0;
  const accountUrl = hibpAccountUrl(status || {}, email);
  const riskTone = unavailable ? 'medium' : clean ? 'clean' : count <= 2 ? 'medium' : 'critical';

  if (loading && !status && count === 0) {
    return (
      <section className="space-y-3" aria-label="Security and data breaches" data-testid="hibp-card-loading">
        <article className={`${FEED_CARD_SHELL} animate-pulse border-emerald-500/20`} aria-busy="true">
          <div className="h-4 w-48 rounded bg-slate-800/80" />
          <div className="mt-3 h-12 rounded-xl bg-slate-900/80" />
        </article>
      </section>
    );
  }

  const badgeLabel = hibpRiskBadgeLabel(count, {
    unavailable,
    statusLabel: status?.status_label,
  });

  return (
    <section className="space-y-3" aria-label="Security and data breaches" data-testid="hibp-card">
      <div className="flex flex-wrap items-end justify-between gap-2 px-1">
        <div>
          <h4 className="text-[10px] font-black uppercase tracking-[0.2em] text-red-300">
            Security & Data Breaches (HIBP)
          </h4>
          <p className="mt-1 text-[11px] text-slate-400">
            Have I Been Pwned breach intelligence for this mailbox
          </p>
        </div>
        <span
          className={`rounded-lg border px-2.5 py-1 text-[10px] font-black uppercase tracking-widest ${
            riskTone === 'medium' && unavailable
              ? 'border-amber-500/40 bg-amber-950/40 text-amber-200'
              : riskTone === 'clean'
                ? 'border-emerald-500/40 bg-emerald-950/40 text-emerald-200'
                : riskTone === 'medium'
                  ? 'border-amber-500/40 bg-amber-950/40 text-amber-200'
                  : 'border-red-500/40 bg-red-950/40 text-red-200'
          }`}
        >
          {badgeLabel}
        </span>
      </div>

      <article
        className={`${FEED_CARD_SHELL} ${
          unavailable
            ? 'border-amber-500/30'
            : clean
              ? 'border-emerald-500/30'
              : riskTone === 'medium'
                ? 'border-amber-500/30'
                : 'border-red-500/30'
        }`}
        data-testid="hibp-status-card"
      >
        <div className="flex min-w-0 flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-5">
          <div className="flex min-w-0 flex-1 items-start gap-4 sm:gap-5">
            <div
              className={`flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-xl border shadow-inner transition-all ${
                unavailable
                  ? 'border-amber-500/30 bg-amber-950/40 text-amber-300'
                  : clean
                    ? 'border-emerald-500/30 bg-emerald-950/40 text-emerald-300'
                    : riskTone === 'medium'
                      ? 'border-amber-500/30 bg-amber-950/40 text-amber-300'
                      : 'border-red-500/30 bg-red-950/50 text-red-400'
              }`}
            >
              <span className="text-2xl" aria-hidden>
                {unavailable ? '!' : clean ? '✓' : '⚠️'}
              </span>
            </div>
            <div className="min-w-0 flex-1">
              <div className="mb-0.5 flex flex-wrap items-center gap-2">
                <span className="flex flex-wrap items-center gap-1 text-[10px] font-black uppercase tracking-[0.15em] text-slate-300">
                  Have I Been Pwned
                  {!clean && !unavailable ? <RiskBadge isAlert isHibp /> : null}
                </span>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <h3 className={FEED_TITLE}>
                  {unavailable
                    ? 'HIBP API unavailable'
                    : clean
                      ? 'No known breach exposure'
                      : `${count} compromised source(s) detected`}
                </h3>
                <MatchBadge value={unavailable ? 60 : clean ? 95 : count <= 2 ? 55 : 40} />
              </div>
              <p className="mt-1 text-[11px] text-slate-400">
                {status?.message ||
                  (unavailable
                    ? 'Could not query the HIBP API. Use Inspect to retry or verify on haveibeenpwned.com.'
                    : clean
                      ? 'No exposed records found in known leaks.'
                      : 'This mailbox appears in one or more public breach datasets. Open Inspect for breach names, dates, and data classes.')}
              </p>
              <a
                href={accountUrl}
                target="_blank"
                rel="noreferrer"
                className={`mt-2 block break-all text-[11px] ${FEED_LINK}`}
              >
                {accountUrl}
              </a>
            </div>
          </div>
          <button
            type="button"
            onClick={() => onInspect?.({ email, status })}
            className={FEED_INSPECT_BTN}
            data-testid="hibp-inspect-btn"
          >
            Inspect
          </button>
        </div>
      </article>
    </section>
  );
}
